import React, { useEffect, useMemo, useState, useRef } from 'react';
import { fmtMoney } from '../utils/formatters';
import { RowData } from '../utils/roiCalculations';
interface ProfitChartProps {
  rows: RowData[];
  breakeven: string;
  halfSales: boolean;
  setHalfSales: (val: boolean) => void;
  doubleAttrition: boolean;
  setDoubleAttrition: (val: boolean) => void;
  onTarget: boolean;
  onToggleOnTarget: () => void;
  plusFive: boolean;
  onPlusFive: () => void;
}
type ChartMode = 'revenue' | 'monthly' | 'roi';
// Helper to calculate nice round numbers for axis labels
function getNiceAxisValues(min: number, max: number): number[] {
  const range = max - min;
  // Determine step size based on range
  let step: number;
  if (range <= 100000) {
    step = 25000; // 25K increments for smaller ranges
  } else if (range <= 250000) {
    step = 50000; // 50K increments
  } else if (range <= 500000) {
    step = 100000; // 100K increments
  } else if (range <= 1000000) {
    step = 200000; // 200K increments
  } else {
    step = 250000; // 250K increments for large ranges
  }
  // Round min down to nearest step
  const axisMin = Math.floor(min / step) * step;
  // Round max up to nearest step
  const axisMax = Math.ceil(max / step) * step;
  // Generate 5 evenly spaced values
  const values: number[] = [];
  for (let i = 0; i <= 4; i++) {
    values.push(axisMin + (axisMax - axisMin) * (i / 4));
  }
  return values;
}
export function ProfitChart({
  rows,
  breakeven,
  halfSales,
  setHalfSales,
  doubleAttrition,
  setDoubleAttrition,
  onTarget,
  onToggleOnTarget,
  plusFive,
  onPlusFive
}: ProfitChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [mode, setMode] = useState<ChartMode>('roi');
  const [timeframe, setTimeframe] = useState<3 | 6 | 12>(12);
  const [mousePos, setMousePos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Apply scenario adjustments to rows
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition) return rows;
    return rows.map((row, idx) => {
      let newRow = {
        ...row
      };
      // Apply half sales scenario
      if (halfSales) {
        newRow.totalSales = row.totalSales * 0.5;
        newRow.grossRevenue = row.grossRevenue * 0.5;
        newRow.grossProfit = row.grossProfit * 0.5;
      }
      // Apply double attrition (half retention) scenario
      if (doubleAttrition) {
        // Halve all active counts
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        // Recalculate sales based on reduced active reps
        // This compounds with halfSales if both are active
        const retentionMultiplier = 0.5;
        const salesMultiplier = halfSales ? 0.5 : 1;
        newRow.totalSales = row.totalSales * retentionMultiplier * salesMultiplier;
        newRow.grossRevenue = row.grossRevenue * retentionMultiplier * salesMultiplier;
        newRow.grossProfit = row.grossProfit * retentionMultiplier * salesMultiplier;
      }
      // Recalculate net profit and cumulative profit
      newRow.netProfit = newRow.grossProfit - row.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition]);
  // Recalculate cumulative profit for adjusted rows
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  // Calculate BASE rows (without scenarios) to determine fixed Y-axis scale
  const baseRows = useMemo(() => {
    let cumProfit = rows[0].cumProfit;
    return rows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [rows]);
  // Calculate cumulative totals for tooltip - use displayRows based on timeframe
  const getCumulativeTotals = (index: number) => {
    // Use displayRows (filtered by timeframe) instead of finalRows
    const displayRows = finalRows.slice(0, timeframe + 1);
    const upToIndex = displayRows.slice(0, index + 1);
    const totalInvestment = upToIndex.reduce((sum, r) => sum + r.totalCost, 0);
    const totalGrossRevenue = upToIndex.reduce((sum, r) => sum + r.grossRevenue, 0);
    const totalGrossProfit = upToIndex.reduce((sum, r) => sum + r.grossProfit, 0);
    const totalNetProfit = upToIndex.reduce((sum, r) => sum + r.netProfit, 0);
    return {
      period: displayRows[index].period,
      totalInvestment,
      totalGrossRevenue,
      totalGrossProfit,
      totalNetProfit,
      monthlyInvestment: displayRows[index].totalCost,
      monthlyGrossProfit: displayRows[index].grossProfit,
      monthlyNetProfit: displayRows[index].netProfit
    };
  };
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = 400 * dpr;
    ctx.scale(dpr, dpr);
    const w = rect.width;
    const h = 400;
    ctx.clearRect(0, 0, w, h);
    const padding = {
      l: 110,
      r: 16,
      t: 20,
      b: 32
    };
    const innerW = w - padding.l - padding.r;
    const innerH = h - padding.t - padding.b;
    // Determine which rows to show based on timeframe (applies to all modes now)
    let displayRows: RowData[];
    let baseDisplayRows: RowData[];
    if (mode === 'roi') {
      // ROI mode: respect timeframe selection (3, 6, or 12 months)
      displayRows = finalRows.slice(0, timeframe + 1);
      baseDisplayRows = baseRows.slice(0, timeframe + 1);
    } else {
      // Other modes: use timeframe for filtering (Launch + selected months)
      displayRows = finalRows.slice(0, timeframe + 1);
      baseDisplayRows = baseRows.slice(0, timeframe + 1);
    }
    let data1: number[];
    let data2: number[] | null = null;
    let data3: number[] | null = null;
    let minV: number, maxV: number;
    // Calculate Min/Max based on DISPLAY rows (filtered by timeframe), not BASE rows
    if (mode === 'revenue') {
      const displayCumulativeGrossRevenue: number[] = [];
      const displayCumulativeNetProfit: number[] = [];
      const displayCumulativeInvestment: number[] = [];
      let sumGrossRevenue = 0;
      let sumNetProfit = 0;
      let sumInvestment = 0;
      displayRows.forEach(r => {
        sumGrossRevenue += r.grossRevenue;
        sumNetProfit += r.netProfit;
        sumInvestment += r.totalCost;
        displayCumulativeGrossRevenue.push(sumGrossRevenue);
        displayCumulativeNetProfit.push(sumNetProfit);
        displayCumulativeInvestment.push(sumInvestment);
      });
      minV = Math.min(0, ...displayCumulativeGrossRevenue, ...displayCumulativeNetProfit, ...displayCumulativeInvestment);
      maxV = Math.max(...displayCumulativeGrossRevenue, ...displayCumulativeNetProfit, ...displayCumulativeInvestment);
      // Actual data to plot
      data1 = displayCumulativeGrossRevenue;
      data2 = displayCumulativeNetProfit;
      data3 = displayCumulativeInvestment;
    } else if (mode === 'monthly') {
      // Monthly mode: show investment vs gross profit for display rows only
      const displayData1 = displayRows.map(r => r.totalCost);
      const displayData2 = displayRows.map(r => r.grossProfit);
      minV = Math.min(0, ...displayData1, ...displayData2);
      maxV = Math.max(...displayData1, ...displayData2);
      // Actual data to plot
      data1 = displayData1;
      data2 = displayData2;
    } else {
      // ROI mode: show cumulative investment vs cumulative gross profit
      const displayCumulativeInvestment: number[] = [];
      const displayCumulativeGrossProfit: number[] = [];
      let sumInvestment = 0;
      let sumGrossProfit = 0;
      displayRows.forEach(r => {
        sumInvestment += r.totalCost;
        sumGrossProfit += r.grossProfit;
        displayCumulativeInvestment.push(sumInvestment);
        displayCumulativeGrossProfit.push(sumGrossProfit);
      });
      minV = Math.min(0, ...displayCumulativeInvestment, ...displayCumulativeGrossProfit);
      maxV = Math.max(...displayCumulativeInvestment, ...displayCumulativeGrossProfit);
      // Actual data to plot
      data1 = displayCumulativeInvestment;
      data2 = displayCumulativeGrossProfit;
    }
    // Get nice axis values based on DISPLAY min/max (dynamically scaled)
    const axisValues = getNiceAxisValues(minV, maxV);
    const lo = axisValues[0];
    const hi = axisValues[axisValues.length - 1];
    const dataLength = displayRows.length;
    const x = (i: number) => padding.l + i / (dataLength - 1) * innerW;
    const y = (v: number) => padding.t + (1 - (v - lo) / (hi - lo)) * innerH;
    // Axes
    ctx.strokeStyle = '#31516a';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding.l, padding.t);
    ctx.lineTo(padding.l, padding.t + innerH);
    ctx.lineTo(padding.l + innerW, padding.t + innerH);
    ctx.stroke();
    // Zero line
    if (lo < 0 && hi > 0) {
      const y0 = y(0);
      ctx.strokeStyle = '#4d7087';
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(padding.l, y0);
      ctx.lineTo(padding.l + innerW, y0);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    if (mode === 'revenue') {
      // Draw cumulative gross revenue line (mint - top line)
      ctx.strokeStyle = '#67E4C1';
      ctx.lineWidth = 3;
      ctx.beginPath();
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
      });
      ctx.stroke();
      // Draw cumulative net profit line (green - middle line)
      if (data2) {
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = 3;
        ctx.beginPath();
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      // Draw cumulative investment line (red - bottom line)
      if (data3) {
        ctx.strokeStyle = '#EF4444';
        ctx.lineWidth = 3;
        ctx.beginPath();
        data3.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      // Points for cumulative gross revenue (mint)
      ctx.fillStyle = '#67E4C1';
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        ctx.beginPath();
        ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
        ctx.fill();
        if (hoveredPoint === i) {
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      // Points for cumulative net profit (green)
      if (data2) {
        ctx.fillStyle = '#10B981';
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
      // Points for cumulative investment (red)
      if (data3) {
        ctx.fillStyle = '#EF4444';
        data3.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
    } else if (mode === 'monthly') {
      // Draw monthly investment line (red)
      ctx.strokeStyle = '#EF4444';
      ctx.lineWidth = 3;
      ctx.beginPath();
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
      });
      ctx.stroke();
      // Draw monthly revenue line (green)
      if (data2) {
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = 3;
        ctx.beginPath();
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      // Points for investment (red)
      ctx.fillStyle = '#EF4444';
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        ctx.beginPath();
        ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
        ctx.fill();
        if (hoveredPoint === i) {
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      // Points for revenue (green)
      if (data2) {
        ctx.fillStyle = '#10B981';
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
    } else {
      // ROI mode
      // Draw cumulative investment line (red)
      ctx.strokeStyle = '#EF4444';
      ctx.lineWidth = 3;
      ctx.beginPath();
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
      });
      ctx.stroke();
      // Draw cumulative gross profit line (green)
      if (data2) {
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = 3;
        ctx.beginPath();
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          if (i === 0) ctx.moveTo(px, py);else ctx.lineTo(px, py);
        });
        ctx.stroke();
      }
      // Find crossover point and highlight it
      if (data2) {
        for (let i = 1; i < data1.length; i++) {
          // Check if cumulative gross profit crosses above cumulative investment (ROI achieved)
          if (data2[i] >= data1[i] && data2[i - 1] < data1[i - 1]) {
            // Crossover detected! Draw a highlight
            const px = x(i);
            const py = y(data2[i]);
            // Draw a highlight circle
            ctx.fillStyle = '#67E4C1';
            ctx.beginPath();
            ctx.arc(px, py, 10, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 3;
            ctx.stroke();
            // Draw vertical dashed line
            ctx.strokeStyle = '#67E4C1';
            ctx.setLineDash([5, 5]);
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(px, padding.t);
            ctx.lineTo(px, padding.t + innerH);
            ctx.stroke();
            ctx.setLineDash([]);
            // Add "ROI Achieved!" label
            ctx.fillStyle = '#67E4C1';
            ctx.font = 'bold 14px Manrope, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('ROI Achieved!', px, padding.t - 5);
            break;
          }
        }
      }
      // Points for investment (red)
      ctx.fillStyle = '#EF4444';
      data1.forEach((v, i) => {
        const px = x(i);
        const py = y(v);
        ctx.beginPath();
        ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
        ctx.fill();
        if (hoveredPoint === i) {
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      // Points for gross profit (green)
      if (data2) {
        ctx.fillStyle = '#10B981';
        data2.forEach((v, i) => {
          const px = x(i);
          const py = y(v);
          ctx.beginPath();
          ctx.arc(px, py, hoveredPoint === i ? 6 : 4, 0, Math.PI * 2);
          ctx.fill();
          if (hoveredPoint === i) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }
    }
    // Y-Axis Labels - use nice axis values
    ctx.fillStyle = '#b6c8d1';
    ctx.font = '600 12px Manrope, sans-serif';
    ctx.textAlign = 'right';
    axisValues.forEach(tv => {
      const py = y(tv);
      ctx.fillText(fmtMoney(tv), padding.l - 8, py + 4);
    });
    // X-Axis Labels
    ctx.fillStyle = '#b6c8d1';
    ctx.font = '600 12px Manrope, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Launch', padding.l, h - 10);
    ctx.textAlign = 'right';
    ctx.fillText(`M${timeframe}`, padding.l + innerW, h - 10);
  }, [finalRows, baseRows, mode, timeframe, hoveredPoint]);
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const padding = {
      l: 110,
      r: 16,
      t: 20,
      b: 32
    };
    const innerW = rect.width - padding.l - padding.r;
    const displayRows = finalRows.slice(0, timeframe + 1);
    const dataLength = displayRows.length;
    // Find closest point
    let closestIndex = -1;
    let closestDist = Infinity;
    displayRows.forEach((_, i) => {
      const px = padding.l + i / (dataLength - 1) * innerW;
      const dist = Math.abs(mouseX - px);
      if (dist < closestDist && dist < 20) {
        closestDist = dist;
        closestIndex = i;
      }
    });
    setHoveredPoint(closestIndex >= 0 ? closestIndex : null);
    setMousePos(closestIndex >= 0 ? {
      x: e.clientX,
      y: e.clientY
    } : null);
  };
  const handleMouseLeave = () => {
    setHoveredPoint(null);
    setMousePos(null);
  };
  const tooltipData = hoveredPoint !== null ? getCumulativeTotals(hoveredPoint) : null;
  return <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200 shadow-lg" data-id="element-296" data-mp="yv8yq705nwj8">
      <div className="flex items-center justify-between mb-3" data-id="element-297" data-mp="yteih5ztwslh">
        <div className="text-sm text-gray-600 font-semibold" data-id="element-298" data-mp="13sr63syiy3p4">
          {mode === 'revenue' ? 'Revenue Analysis' : mode === 'roi' ? `ROI Break-Even Analysis (${timeframe} Month${timeframe > 1 ? 's' : ''})` : 'Monthly Investment vs. Monthly Profit'}
        </div>

        <div className="flex gap-2" data-id="element-299" data-mp="1hjm7kk6p1iio">
          {/* Timeframe Toggle - now shows for all modes */}
          <div className="flex gap-1 bg-white border-2 border-gray-200 rounded p-1" data-id="element-300" data-mp="1mipe692bnwsr">
            <button onClick={() => setTimeframe(3)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 3 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-301" data-mp="1wqbhkd8s87q6">
              3M
            </button>
            <button onClick={() => setTimeframe(6)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 6 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-302" data-mp="1iqahh00cd4br">
              6M
            </button>
            <button onClick={() => setTimeframe(12)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 12 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-303" data-mp="25on5g7i9krex">
              12M
            </button>
          </div>

          {/* Mode Toggle */}
          <div className="flex gap-2 bg-white border-2 border-gray-200 rounded p-1" data-id="element-304" data-mp="2tdpwu72p9ymm">
            <button onClick={() => setMode('monthly')} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${mode === 'monthly' ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-305" data-mp="nv8tuvoyazf8">
              Monthly
            </button>
            <button onClick={() => setMode('roi')} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${mode === 'roi' ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-306" data-mp="9vzlgi6zl9lt">
              ROI
            </button>
            <button onClick={() => setMode('revenue')} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${mode === 'revenue' ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-307" data-mp="xrvdjh5jjkfo">
              Revenue
            </button>
          </div>
        </div>
      </div>

      {/* Scenario Toggles */}
      <div className="flex gap-3 mb-3" data-id="element-308" data-mp="1stdskmp44lxr">
        <button onClick={() => setHalfSales(!halfSales)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${halfSales ? 'bg-red-500 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'}`} data-id="element-309" data-mp="1sufejm7o2su9">
          {halfSales ? '✓ ' : ''}50% Sales Scenario
        </button>
        <button onClick={() => setDoubleAttrition(!doubleAttrition)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${doubleAttrition ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`} data-id="element-310" data-mp="2bs865scubgcc">
          {doubleAttrition ? '✓ ' : ''}2x Attrition Scenario
        </button>
        <button onClick={onPlusFive} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${plusFive ? 'bg-blue-500 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'} active:translate-y-[1px]`} data-id="element-311" data-mp="2qj7ed29rk2ve">
          {plusFive ? '✓ ' : ''}+5%
        </button>
        <button onClick={onToggleOnTarget} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${onTarget ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`} data-id="element-312" data-mp="vf99jc26hmoc">
          {onTarget ? '✓ ' : ''}On Target
        </button>
      </div>

      {mode === 'monthly' && <div className="flex gap-4 mb-3 text-xs" data-id="element-313" data-mp="1e58ylyy0r1rr">
          <div className="flex items-center gap-2" data-id="element-314" data-mp="1e6tqfgsy86lx">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-315" data-mp="1s379uvh1mi31"></div>
            <span className="text-gray-600 font-semibold" data-id="element-316" data-mp="1o2wrskfz817t">
              Monthly Investment
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-317" data-mp="3tutu8e6fjeqj">
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-318" data-mp="3txevp22tfsnr"></div>
            <span className="text-gray-600 font-semibold" data-id="element-319" data-mp="3pvkxd0g3f0qr">
              Monthly Gross Profit
            </span>
          </div>
        </div>}

      {mode === 'revenue' && <div className="flex gap-4 mb-3 text-xs" data-id="element-320" data-mp="3jqlqhah90ywv">
          <div className="flex items-center gap-2" data-id="element-321" data-mp="3jthr56khe1p0">
            <div className="w-3 h-3 rounded-full bg-amber" data-id="element-322" data-mp="35zgx2q4h52uz"></div>
            <span className="text-gray-600 font-semibold" data-id="element-323" data-mp="3az37yswty70m">
              Cumulative Gross Revenue
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-324" data-mp="2w1awqcybg4s8">
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-325" data-mp="3115p6kxqp2fe"></div>
            <span className="text-gray-600 font-semibold" data-id="element-326" data-mp="312gtclv3kc71">
              Cumulative Net Profit
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-327" data-mp="2s0hado0iuy1g">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-328" data-mp="2s1aaw78w0rb5"></div>
            <span className="text-gray-600 font-semibold" data-id="element-329" data-mp="l9hvcye0vq0t">
              Cumulative Investment
            </span>
          </div>
        </div>}

      {mode === 'roi' && <div className="flex gap-4 mb-3 text-xs" data-id="element-330" data-mp="2mc8te4mggoao">
          <div className="flex items-center gap-2" data-id="element-331" data-mp="2maxq05wx9ik3">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-332" data-mp="2hdfhggmth5cm"></div>
            <span className="text-gray-600 font-semibold" data-id="element-333" data-mp="2hb328b8g1939">
              Cumulative Investment
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-334" data-mp="28crecgyyopei">
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-335" data-mp="28by2x2p25bq1"></div>
            <span className="text-gray-600 font-semibold" data-id="element-336" data-mp="23do6ypvie73d">
              Cumulative Gross Profit
            </span>
          </div>
        </div>}

      <div ref={containerRef} className="relative" data-id="element-337" data-mp="1ykleym6dr5pa">
        <canvas ref={canvasRef} className="w-full h-[400px] block cursor-crosshair" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} data-id="element-338" data-mp="1tomuh9o6a7u5" />

        {/* Tooltip */}
        {tooltipData && mousePos && <div className="fixed z-50 pointer-events-none" style={{
        left: mousePos.x - 300,
        top: mousePos.y - 16
      }} data-id="element-339" data-mp="1fntuwaj0kmk6">
            <div className="bg-navy text-white rounded shadow-xl border-2 border-amber p-4 min-w-[280px]" data-id="element-340" data-mp="3rdib3z4qrb3x">
              <div className="font-bold text-amber mb-2 text-sm" data-id="element-341" data-mp="46g4la2lic97">
                {tooltipData.period}
              </div>

              {mode === 'revenue' ? <>
                  <div className="space-y-1.5 text-xs" data-id="element-342" data-mp="936mm2xynhx9">
                    <div className="flex justify-between gap-4" data-id="element-343" data-mp="38lz5rln8vkyq">
                      <span className="text-gray-300" data-id="element-344" data-mp="3clzsruoet4he">
                        Cumulative Gross Revenue:
                      </span>
                      <span className="font-bold text-amber" data-id="element-345" data-mp="3hkkl77xf6jva">
                        {fmtMoney(tooltipData.totalGrossRevenue)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4" data-id="element-346" data-mp="2oxgmtlsu13sh">
                      <span className="text-gray-300" data-id="element-347" data-mp="2tvrxtzuv5kca">
                        Cumulative Net Profit:
                      </span>
                      <span className={`font-bold ${tooltipData.totalNetProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-348" data-mp="10hx1jojxtrn">
                        {fmtMoney(tooltipData.totalNetProfit)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4" data-id="element-349" data-mp="5rhvwuh8elws">
                      <span className="text-gray-300" data-id="element-350" data-mp="5udsaedfl2j4">
                        Cumulative Investment:
                      </span>
                      <span className="font-bold text-red-400" data-id="element-351" data-mp="3999skm6bbu1b">
                        {fmtMoney(tooltipData.totalInvestment)}
                      </span>
                    </div>
                    <div className="border-t border-gray-600 my-1.5" data-id="element-352" data-mp="3i9epg2dvra18"></div>
                    <div className="flex justify-between gap-4" data-id="element-353" data-mp="3ibj1x8puhhd4">
                      <span className="text-gray-300" data-id="element-354" data-mp="12h8x1ck137bk">Gross Margin:</span>
                      <span className="font-bold text-amber" data-id="element-355" data-mp="xjpcjuwqlyb6">
                        {fmtMoney(tooltipData.totalGrossRevenue - tooltipData.totalInvestment)}
                      </span>
                    </div>
                  </div>
                </> : mode === 'monthly' ? <>
                  <div className="space-y-1.5 text-xs" data-id="element-356" data-mp="110lfexio9v2e">
                    <div className="flex justify-between gap-4" data-id="element-357" data-mp="111d9u50j9mr3">
                      <span className="text-gray-300" data-id="element-358" data-mp="1orqvndsiypd8">Monthly Investment:</span>
                      <span className="font-bold text-red-400" data-id="element-359" data-mp="1tqah7d37hz52">
                        {fmtMoney(tooltipData.monthlyInvestment)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4" data-id="element-360" data-mp="1jzpm3zmqrcqw">
                      <span className="text-gray-300" data-id="element-361" data-mp="1jy4p83cyfhpv">
                        Monthly Gross Profit:
                      </span>
                      <span className="font-bold text-green-400" data-id="element-362" data-mp="2cg0blz324iwg">
                        {fmtMoney(tooltipData.monthlyGrossProfit)}
                      </span>
                    </div>
                    <div className="border-t border-gray-600 my-1.5" data-id="element-363" data-mp="cfvbrb4m6bm8"></div>
                    <div className="flex justify-between gap-4" data-id="element-364" data-mp="7lzlyvyi7v25">
                      <span className="text-gray-300" data-id="element-365" data-mp="2p9546evkvfo">Monthly ROI:</span>
                      <span className={`font-bold ${tooltipData.monthlyGrossProfit >= tooltipData.monthlyInvestment ? 'text-green-400' : 'text-red-400'}`} data-id="element-366" data-mp="3usa2qqtqhqei">
                        {tooltipData.monthlyInvestment > 0 ? `${((tooltipData.monthlyGrossProfit - tooltipData.monthlyInvestment) / tooltipData.monthlyInvestment * 100).toFixed(1)}%` : 'N/A'}
                      </span>
                    </div>
                    <div className="border-t border-gray-600 my-1.5" data-id="element-367" data-mp="3725vm9hc78ir"></div>
                    <div className="flex justify-between gap-4" data-id="element-368" data-mp="3703059xf0n6n">
                      <span className="text-gray-300" data-id="element-369" data-mp="321rp4vvdw6mu">
                        Cumulative Net Profit:
                      </span>
                      <span className={`font-bold ${tooltipData.totalNetProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-370" data-mp="1hjh7j7pjeon9">
                        {fmtMoney(tooltipData.totalNetProfit)}
                      </span>
                    </div>
                  </div>
                </> : <>
                  <div className="space-y-1.5 text-xs" data-id="element-371" data-mp="20b9y6vh4vxrt">
                    <div className="flex justify-between gap-4" data-id="element-372" data-mp="25ad4skcric1w">
                      <span className="text-gray-300" data-id="element-373" data-mp="25cpl6t2vgecu">Total Invested:</span>
                      <span className="font-bold text-red-400" data-id="element-374" data-mp="2aahbjinvbsex">
                        {fmtMoney(tooltipData.totalInvestment)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4" data-id="element-375" data-mp="fx9owea1qn4d">
                      <span className="text-gray-300" data-id="element-376" data-mp="jxa07q1umi77">Total Gross Profit:</span>
                      <span className="font-bold text-green-400" data-id="element-377" data-mp="jzcz6zkzblo2">
                        {fmtMoney(tooltipData.totalGrossProfit)}
                      </span>
                    </div>
                    <div className="border-t border-gray-600 my-1.5" data-id="element-378" data-mp="ac4y9vp81kl9"></div>
                    <div className="flex justify-between gap-4" data-id="element-379" data-mp="3spm94zd8y1jy">
                      <span className="text-gray-300" data-id="element-380" data-mp="jqt0syxpvuc">Net Position:</span>
                      <span className={`font-bold ${tooltipData.totalGrossProfit >= tooltipData.totalInvestment ? 'text-green-400' : 'text-red-400'}`} data-id="element-381" data-mp="o9th6xzqahno">
                        {fmtMoney(tooltipData.totalGrossProfit - tooltipData.totalInvestment)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4" data-id="element-382" data-mp="2zz47m2s6888n">
                      <span className="text-gray-300" data-id="element-383" data-mp="301qbnsfus5m4">ROI:</span>
                      <span className={`font-bold ${tooltipData.totalGrossProfit >= tooltipData.totalInvestment ? 'text-green-400' : 'text-red-400'}`} data-id="element-384" data-mp="3501n27lsqo6g">
                        {tooltipData.totalInvestment > 0 ? `${((tooltipData.totalGrossProfit - tooltipData.totalInvestment) / tooltipData.totalInvestment * 100).toFixed(1)}%` : 'N/A'}
                      </span>
                    </div>
                    {tooltipData.totalGrossProfit >= tooltipData.totalInvestment && <div className="text-green-400 font-bold text-center mt-2" data-id="element-385" data-mp="28tgisqj8opd3">
                        ✓ ROI Achieved!
                      </div>}
                  </div>
                </>}
            </div>
          </div>}
      </div>

      <div className="text-sm text-gray-600 mt-3" data-id="element-386" data-mp="1uzfi3r8q8sa3">
        {mode === 'revenue' ? <>
            <span className="font-semibold" data-id="element-387" data-mp="1q0kyin2xqzox">Break-even:</span> {breakeven}
          </> : mode === 'roi' ? <span className="text-gray-600" data-id="element-388" data-mp="3q33xpw33d5ci">
            Showing when cumulative gross profit exceeds cumulative investment
            (ROI achieved)
          </span> : <span className="text-gray-600" data-id="element-389" data-mp="3p3wz3wqhse9">
            Comparing monthly investment to monthly gross profit
          </span>}
      </div>
    </div>;
}